import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { downloadFromApi } from 'src/common/utils/api/downloadFromApi';

/**
 * Revenue as the back end works it out (src/admin/domain/revenue.py). Nothing here prices anything:
 * the tab renders these figures as they arrive, so every view of revenue agrees.
 */

export type BreakdownKey =
  | 'ADVISER_PER_UNIT'
  | 'ADVISER_FIXED'
  | 'MONUMENT_BUILD'
  | 'MONUMENT_LICENSE'
  | 'PIPELINE';

/** How far a figure is from money billed today. */
export type FigureKind = 'ACTUAL' | 'MANUAL' | 'WHAT_IF' | 'FORECAST';

export type BreakdownLine = {
  key: BreakdownKey;
  label: string;
  mrrPence: number;
  arrPence: number;
  kind: FigureKind;
  kindLabel: string;
  included: boolean;
};

type ModelledFirm = {
  companyId: string;
  name: string;
  isPipeline: boolean;
  arrangement: string;
  units: number | null;
  unitPricePence: number | null;
  minimumPence: number | null;
  monthlyBillPence: number;
  mrrPence: number;
  breakdown: BreakdownKey;
};

/** Monument's settings as the scenario priced them. */
type ResolvedMonument = {
  buildFeeOn: boolean;
  buildFeePence: number;
  licenseFeePence: number;
  customers: number;
};

export type ScenarioResult = {
  mrrPence: number;
  arrPence: number;
  breakdown: BreakdownLine[];
  firms: ModelledFirm[];
  monument: ResolvedMonument;
  standardUnitPricePence: number;
  /** The firm changes the back end applied: in scope and changing something. */
  overrides: {
    companyId: string;
    name: string;
    excluded: boolean;
    units: number | null;
    unitPricePence: number | null;
  }[];
};

export type MonumentRevenue = {
  buildPence: number;
  licensePence: number;
  totalPence: number;
};

type PipelineFirm = {
  companyId: string;
  name: string;
  statedUnits: number | null;
  agreedPence: number | null;
  fullAdoptionPence: number | null;
};

export type RevenueStep = {
  label: string;
  arrPence: Record<BreakdownKey, number>;
};

export type RevenueOverview = {
  today: ScenarioResult;
  fullAdoption: ScenarioResult;
  monumentToday: MonumentRevenue;
  monumentFullAdoption: MonumentRevenue;
  billedUnits: number;
  contractedNotBillingPence: number;
  /** The free-trial firms at full adoption, as the back end's scenario model prices them. */
  pipelineFullAdoptionPence: number;
  firmsWithoutStatedUnits: number;
  pipeline: PipelineFirm[];
  steps: RevenueStep[];
};

export type MonthRevenue = {
  /** First of the month, YYYY-MM-DD. */
  month: string;
  billedUnits: number;
  perUnitMrrPence: number;
  fixedMrrPence: number;
  adviserMrrPence: number;
};

export type MonumentTier = { upTo: number | null; ratePence: number };

export type MonumentTerms = {
  buildFeePence: number;
  buildFeeStarted: boolean;
  /** Charged each month once Monument has a customer, on top of the per-customer rates. */
  licenseFeePence: number;
  customerCount: number;
  fullAdoptionCustomerCount: number;
  tiers: MonumentTier[];
};

export const lineOf = (result: ScenarioResult, key: BreakdownKey): BreakdownLine =>
  result.breakdown.find(line => line.key === key)!;

export const useRevenueOverview = (includeInternal: boolean) =>
  useQuery<RevenueOverview>({
    queryKey: ['admin', 'revenue', { include_internal: includeInternal }],
  });

export const useRevenueHistory = (includeInternal: boolean) =>
  useQuery<MonthRevenue[]>({
    queryKey: ['admin', 'revenue', 'history', { include_internal: includeInternal }],
  });

export const useMonumentTerms = () =>
  useQuery<MonumentTerms>({ queryKey: ['admin', 'revenue', 'monument'] });

export const useSaveMonumentTerms = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<{ data: unknown }, Error, MonumentTerms>({
    mutationFn: terms => api.put('admin/revenue/monument', terms),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'revenue'] }),
  });
  return { saveMonumentTerms: mutateAsync, isSaving: isPending };
};

export type FirmOverride = { excluded?: boolean; units?: number; unitPricePence?: number };

/**
 * The assumptions a scenario changes, and nothing else: anything left out is today's figure, read
 * fresh by the back end, so a Monument fee saved meanwhile still reaches an open scenario.
 */
export type ScenarioPatch = {
  standardUnitPricePence?: number;
  adviserFullAdoption?: boolean;
  monumentFullAdoption?: boolean;
  includeMonument?: boolean;
  monumentBuildFeeOn?: boolean;
  monumentBuildFeePence?: number;
  monumentLicenseFeePence?: number;
  monumentCustomerCount?: number;
  includePipeline?: boolean;
  firmOverrides?: Record<string, FirmOverride>;
};

export const useRevenueScenario = (
  patch: ScenarioPatch,
  includeInternal: boolean,
  enabled: boolean,
) =>
  useQuery<ScenarioResult>({
    enabled,
    queryKey: ['admin', 'revenue', 'scenario', patch, includeInternal],
    queryFn: async () =>
      (await api.post<ScenarioResult>('admin/revenue/scenario', { ...patch, includeInternal }))
        .data,
    placeholderData: keepPreviousData,
  });

export const useScenarioExport = () => {
  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    { patch: ScenarioPatch; includeInternal: boolean }
  >({
    mutationFn: ({ patch, includeInternal }) =>
      downloadFromApi('/admin/revenue/scenario/export', 'revenue-scenario.xlsx', {
        ...patch,
        includeInternal,
      }),
  });
  return { exportScenario: mutateAsync, isExporting: isPending };
};
